import React from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Tooltip, IconButton, TextField, Stack } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import { collection, getDocs, limit as fsLimit, orderBy, query, startAfter } from 'firebase/firestore';
import { db } from '../FirebaseConfig';
import handleLogout from '../Services/signOut';

function formatTs(ts) {
  try {
    if (!ts) return '';
    if (typeof ts.toDate === 'function') return ts.toDate().toLocaleString('pt-BR');
    const d = new Date(ts);
    // Fallback: ISO/locale
    return isNaN(d.getTime()) ? String(ts) : d.toLocaleString('pt-BR');
  } catch (_e) {
    return '';
  }
}

const PAGE_SIZE = 50;

export default function AdminPanel() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [cursor, setCursor] = React.useState(null);
  const [emailFilter, setEmailFilter] = React.useState('');

  const fetchPage = React.useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const base = collection(db, 'audit_logs');
      const constraints = [orderBy('created_at', 'desc'), fsLimit(PAGE_SIZE)];
      const q = reset || !cursor ? query(base, ...constraints) : query(base, ...constraints, startAfter(cursor));
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCursor(snap.docs[snap.docs.length - 1] || null);
      setRows((prev) => (reset ? docs : [...prev, ...docs]));
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  React.useEffect(() => {
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = React.useMemo(() => {
    if (!emailFilter) return rows;
    const needle = emailFilter.toLowerCase();
    return rows.filter((r) => String(r.user_email || '').toLowerCase().includes(needle));
  }, [rows, emailFilter]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Painel de Auditoria (Super Admin)</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField size="small" placeholder="Filtrar por email" value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} />
          <Tooltip title="Recarregar">
            <span>
              <IconButton onClick={() => { setCursor(null); fetchPage(true); }} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Sair'>
            <IconButton onClick={async () => { await handleLogout(); window.location.href = '/'; }}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Bucket</TableCell>
              <TableCell>Objeto</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">Sem registros</TableCell>
              </TableRow>
            ) : (
              filtered.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{formatTs(r.created_at)}</TableCell>
                  <TableCell>{r.user_email}</TableCell>
                  <TableCell>{r.s3_bucket}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 640 }} noWrap title={r.s3_key}>{r.s3_key}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <Button variant="outlined" onClick={() => fetchPage(false)} disabled={loading || !cursor}>
          {loading ? 'Carregando...' : (cursor ? 'Carregar mais' : 'Fim dos registros')}
        </Button>
      </Box>
    </Box>
  );
}


